import { clsx } from 'clsx';
import type { TicketStatus } from '@centraoneplus/shared-types';

interface StatusBadgeProps {
  status: TicketStatus;
  size?: 'sm' | 'md' | 'lg';
  /** Show the leading status glyph. Color alone never carries meaning. */
  showGlyph?: boolean;
  className?: string;
}

/**
 * Ivory & Brass status badge.
 *
 * Changes from the previous version:
 * - Square (2px), not a pill.
 * - Mono uppercase with tracking — status is metadata, so it speaks in the
 *   metadata voice.
 * - Five statuses map onto four semantic treatments. The old version used
 *   blue/indigo/orange/green/gray, which read as five unrelated things.
 *   Here: new is brass, active is green with a pulsing mark, waiting is
 *   terracotta, resolved is green, closed is plain ink. That is the actual
 *   information.
 * - A glyph accompanies the color (constitution: never color alone).
 */
const statusConfig: Record<
  TicketStatus,
  { label: string; glyph: string; color: string; pulse?: boolean }
> = {
  new: {
    label: 'New',
    glyph: '◆',
    color:
      'text-[var(--co-brass-ink)] border-[var(--co-brass)] bg-[var(--co-brass-wash)]',
  },
  in_progress: {
    label: 'Active',
    glyph: '◉',
    // Work in flight reads green, like the mockup — and the glyph pulses, which
    // is what tells it apart from the (also green) Resolved.
    pulse: true,
    color:
      'text-[var(--co-green)] border-[var(--co-green)] bg-[var(--co-green-wash)]',
  },
  pending: {
    label: 'Waiting',
    glyph: '◑',
    color:
      'text-[var(--co-terracotta)] border-[var(--co-terracotta)] bg-[var(--co-terracotta-wash)]',
  },
  resolved: {
    label: 'Resolved',
    glyph: '✓',
    color:
      'text-[var(--co-green)] border-[var(--co-green)] bg-[var(--co-green-wash)]',
  },
  closed: {
    label: 'Closed',
    glyph: '—',
    color:
      'text-[var(--co-ink-3)] border-[var(--co-line-strong)] bg-transparent',
  },
};

/**
 * Explicit heights, not padding. StatusBadge and PriorityBadge's chip variant
 * sit side by side in the ticket header plate, so they have to resolve to the
 * same box — padding-driven sizing let them drift (the priority chip rendered
 * visibly taller). These values are shared with PriorityBadge; change both.
 */
const sizeClasses = {
  sm: 'h-[20px] px-[7px] text-[9px] tracking-[.13em]',
  md: 'h-[24px] px-[9px] text-[10px] tracking-[.13em]',
  lg: 'h-[26px] px-[11px] text-[11px] tracking-[.12em]',
};

export function StatusBadge({
  status,
  size = 'md',
  showGlyph = true,
  className,
}: StatusBadgeProps) {
  const config =
    statusConfig[status] || {
      label: String(status).replace(/_/g, ' '),
      glyph: '·',
      color: 'text-[var(--co-ink-3)] border-[var(--co-line-strong)] bg-transparent',
    };

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-[5px] rounded-[2px] border font-mono font-semibold uppercase leading-none',
        config.color,
        sizeClasses[size],
        className,
      )}
    >
      {showGlyph && (
        <span aria-hidden="true" className={config.pulse ? 'animate-pulse' : undefined}>
          {config.glyph}
        </span>
      )}
      {config.label}
    </span>
  );
}

export default StatusBadge;
